import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { timeEntriesApi } from '../../api/timeEntries';
import toast from 'react-hot-toast';

interface TimeEntryFormProps {
  ticketId: string;
}

export default function TimeEntryForm({ ticketId }: TimeEntryFormProps) {
  const queryClient = useQueryClient();
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);

  const createMutation = useMutation({
    mutationFn: () =>
      timeEntriesApi.create({
        ticketId,
        durationMinutes: Math.round(parseFloat(duration) * 60) || 0,
        description: description.trim() || 'Work performed',
        billable,
      }),
    onSuccess: () => {
      setDuration('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['time-entries', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      toast.success('Time logged');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const mins = Math.round(parseFloat(duration) * 60);
        if (mins > 0) createMutation.mutate();
        else toast.error('Enter a valid duration');
      }}
      className="space-y-3"
    >
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="label">Duration (hours)</label>
          <input
            type="number"
            step="0.25"
            min="0.25"
            className="input"
            placeholder="0.5"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="rounded" />
            <span className="text-sm">Billable</span>
          </label>
        </div>
      </div>
      <div>
        <label className="label">Description</label>
        <textarea
          className="input min-h-[60px] resize-y"
          placeholder="What did you work on?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || !duration}>
        Log time
      </button>
    </form>
  );
}
